import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Leaf, Play, ExternalLink, Phone } from 'lucide-react';
import {
  TRAUMA_RESPONSES,
  GROUNDING_TECHNIQUES,
  TRAUMA_VIDEOS,
  TRAUMA_SERVICES,
  POST_TRAUMA_SYMPTOMS,
} from '../data/content';
import PrintButton from '../components/PrintButton';
import SEO from '../components/SEO';
import HelplinesStrip from '../components/HelplinesStrip';

function VideoCard({ video }) {
  const [playing, setPlaying] = useState(false);
  const embedUrl = `https://www.youtube.com/embed/${video.youtubeId}?rel=0&modestbranding=1&playsinline=1&autoplay=1`;
  const thumb = `https://img.youtube.com/vi/${video.youtubeId}/hqdefault.jpg`;
  const watchUrl = `https://www.youtube.com/watch?v=${video.youtubeId}`;

  return (
    <div className="tf-card overflow-hidden" data-testid={`video-${video.id}`}>
      <div className="relative" style={{ aspectRatio: '16 / 9', background: '#000' }}>
        {playing ? (
          <iframe
            src={embedUrl}
            title={video.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 w-full h-full"
            style={{ border: 0 }}
          />
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            data-testid={`play-${video.id}`}
            className="absolute inset-0 w-full h-full group"
          >
            <img src={thumb} alt={video.title} className="w-full h-full object-cover" />
            <div
              className="absolute inset-0 flex items-center justify-center"
              style={{ background: 'rgba(0,0,0,0.18)' }}
            >
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform"
                style={{
                  background: 'var(--tf-purple)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                }}
              >
                <Play size={22} fill="#fff" color="#fff" />
              </div>
            </div>
          </button>
        )}
      </div>
      <div className="p-5">
        <p
          className="font-body font-bold"
          style={{ fontSize: 11, letterSpacing: '0.06em', color: 'var(--tf-purple)' }}
        >
          {video.source}
          {video.duration ? ` · ${video.duration}` : ''}
        </p>
        <h4
          className="font-heading mt-2"
          style={{ fontSize: 16.5, color: 'var(--tf-text)', letterSpacing: '-0.01em' }}
        >
          {video.title}
        </h4>
        <p
          className="font-body mt-2"
          style={{ fontSize: 13, color: 'var(--tf-text-secondary)', lineHeight: 1.55 }}
        >
          {video.summary}
        </p>
        <a
          href={watchUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 mt-3 font-bold"
          style={{ color: 'var(--tf-purple)', fontSize: 12.5, textDecoration: 'none' }}
        >
          <ExternalLink size={13} /> Watch on YouTube
        </a>
      </div>
    </div>
  );
}

function GroundingCard({ exercise }) {
  const [open, setOpen] = useState(false);
  return (
    <button
      type="button"
      onClick={() => setOpen(!open)}
      className="tf-card p-5 w-full text-left"
      data-testid={`grounding-${exercise.id}`}
    >
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: 'var(--tf-bg-secondary)', color: 'var(--tf-purple)' }}
        >
          <Leaf size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-heading" style={{ fontSize: 16, color: 'var(--tf-text)' }}>
            {exercise.title}
          </p>
          <p
            className="font-body"
            style={{ fontSize: 11.5, color: 'var(--tf-text-muted)', marginTop: 1 }}
          >
            {exercise.duration}
          </p>
        </div>
        {open ? <ChevronUp size={18} color="var(--tf-text-muted)" /> : <ChevronDown size={18} color="var(--tf-text-muted)" />}
      </div>
      <div
        className="mt-4 pt-4 print-show"
        data-print-expand="true"
        style={{
          borderTop: '1px solid var(--tf-card-border)',
          display: open ? 'flex' : 'none',
          flexDirection: 'column',
          gap: 12,
        }}
      >
          {exercise.steps.map((s, i) => (
            <div key={i} className="flex gap-3 items-start">
              <div
                className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: 'var(--tf-purple-light)', color: 'var(--tf-purple-dark)', fontSize: 11, fontWeight: 700, fontFamily: 'Nunito' }}
              >
                {i + 1}
              </div>
              <p
                className="font-body flex-1"
                style={{ fontSize: 13.5, lineHeight: 1.6, color: 'var(--tf-text-secondary)' }}
              >
                {s}
              </p>
            </div>
          ))}
        </div>
    </button>
  );
}

export default function Trauma() {
  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10" data-testid="trauma-screen">
      <SEO
        title="Trauma & Healing — Survivor Guide UK"
        description="Why your body reacted the way it did — the 5 trauma responses, common after-effects, short explainer videos, grounding tools and UK specialist help."
        path="/app/trauma"
      />
      <p className="eyebrow">TRAUMA &amp; HEALING</p>
      <h1
        className="font-heading mt-3"
        style={{ fontSize: 'clamp(28px, 4vw, 36px)', lineHeight: 1.15, color: 'var(--tf-text)', letterSpacing: '-0.01em' }}
      >
        Trauma responses are normal.
      </h1>
      <p
        className="font-body mt-4 max-w-xl"
        style={{ fontSize: 15, color: 'var(--tf-text-secondary)', lineHeight: 1.65 }}
      >
        Your body and brain did exactly what they were designed to do — survive. Whatever you felt,
        did, or didn&apos;t do, was not your fault. This page explains common trauma responses,
        gives you grounding tools, and shows where to get specialist help in the UK.
      </p>

      <HelplinesStrip variant="sv" />

      <div className="mt-6 flex gap-2 flex-wrap">
        <PrintButton label="Print / Save this page" />
      </div>

      {/* 5 Fs */}
      <div className="mt-10 mb-4">
        <p className="eyebrow">WHAT YOUR BRAIN DID</p>
        <h2 className="font-heading mt-2" style={{ fontSize: 22, color: 'var(--tf-text)' }}>
          The 5 trauma responses
        </h2>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {TRAUMA_RESPONSES.map((r) => (
          <div key={r.id} className="tf-card p-5" data-testid={`response-${r.id}`}>
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center mb-3"
              style={{ background: 'var(--tf-purple-light)' }}
            >
              <span
                className="font-heading"
                style={{ fontSize: 18, color: 'var(--tf-purple-dark)' }}
              >
                {r.letter}
              </span>
            </div>
            <h3 className="font-heading" style={{ fontSize: 18, color: 'var(--tf-text)' }}>
              {r.name}
            </h3>
            <p
              className="font-body font-bold"
              style={{ fontSize: 12, color: 'var(--tf-purple)', letterSpacing: '0.02em', marginTop: 2 }}
            >
              {r.short}
            </p>
            <p
              className="font-body mt-2.5"
              style={{ fontSize: 13, color: 'var(--tf-text-secondary)', lineHeight: 1.55 }}
            >
              {r.body}
            </p>
          </div>
        ))}
      </div>

      {/* Symptoms */}
      <div className="mt-10 p-6 rounded-2xl" style={{ background: 'var(--tf-pink-soft)' }}>
        <h3 className="font-heading" style={{ fontSize: 19, color: 'var(--tf-text)' }}>
          Common after-effects
        </h3>
        <p
          className="font-body mt-2"
          style={{ fontSize: 13, color: 'var(--tf-text)', lineHeight: 1.55 }}
        >
          In the days, weeks or months after trauma, many survivors notice some of the following.
          These are normal nervous-system responses — not personal failings.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {POST_TRAUMA_SYMPTOMS.map((s, i) => (
            <div
              key={i}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full"
              style={{ background: '#fff' }}
            >
              <span
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: 2.5,
                  background: 'var(--tf-purple)',
                  display: 'inline-block',
                }}
              />
              <span className="font-body" style={{ fontSize: 12, color: 'var(--tf-text)' }}>
                {s}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Videos */}
      <div className="mt-12 mb-4">
        <p className="eyebrow">WATCH &amp; LEARN</p>
        <h2 className="font-heading mt-2" style={{ fontSize: 22, color: 'var(--tf-text)' }}>
          Short videos that explain trauma
        </h2>
        <p
          className="font-body mt-2"
          style={{ fontSize: 13.5, color: 'var(--tf-text-secondary)', lineHeight: 1.55 }}
        >
          Tap a video to play. These are short, plain-language explainers from UK and
          trauma-education sources. Some viewers find this content emotional — please pause whenever
          you need.
        </p>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {TRAUMA_VIDEOS.map((v) => (
          <VideoCard key={v.id} video={v} />
        ))}
      </div>

      {/* Grounding */}
      <div className="mt-12 mb-4">
        <p className="eyebrow">FEELING OVERWHELMED?</p>
        <h2 className="font-heading mt-2" style={{ fontSize: 22, color: 'var(--tf-text)' }}>
          Grounding techniques
        </h2>
        <p
          className="font-body mt-2"
          style={{ fontSize: 13.5, color: 'var(--tf-text-secondary)', lineHeight: 1.55 }}
        >
          Use any of these if you feel panicked, dissociated, or stuck in a flashback. They work by
          gently bringing your nervous system back to the present moment.
        </p>
      </div>
      <div className="flex flex-col gap-3">
        {GROUNDING_TECHNIQUES.map((g) => (
          <GroundingCard key={g.id} exercise={g} />
        ))}
      </div>

      {/* Services */}
      <div className="mt-12 mb-4">
        <p className="eyebrow">GETTING SPECIALIST HELP IN THE UK</p>
        <h2 className="font-heading mt-2" style={{ fontSize: 22, color: 'var(--tf-text)' }}>
          Where to find trauma support
        </h2>
        <p
          className="font-body mt-2"
          style={{ fontSize: 13.5, color: 'var(--tf-text-secondary)', lineHeight: 1.55 }}
        >
          Trauma-focused therapy works. NICE (the NHS clinical-guidelines body) recommends
          trauma-focused CBT and EMDR as first-line treatments for PTSD. You do not need to have
          reported to access any of these services.
        </p>
      </div>
      <div className="flex flex-col gap-3">
        {TRAUMA_SERVICES.map((s) => (
          <div key={s.id} className="tf-card p-5" data-testid={`service-${s.id}`}>
            <h3 className="font-heading" style={{ fontSize: 17, color: 'var(--tf-text)' }}>
              {s.name}
            </h3>
            <p
              className="font-body mt-2"
              style={{ fontSize: 13, color: 'var(--tf-text-secondary)', lineHeight: 1.6 }}
            >
              {s.body}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {s.phone && (
                <a
                  href={`tel:${s.phone}`}
                  data-testid={`service-call-${s.id}`}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full font-bold"
                  style={{ background: 'var(--tf-purple)', color: '#fff', fontSize: 12, textDecoration: 'none' }}
                >
                  <Phone size={12} /> {s.phoneLabel || 'Call'}
                </a>
              )}
              {s.website && (
                <a
                  href={s.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`service-web-${s.id}`}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full font-bold"
                  style={{
                    background: 'var(--tf-bg)',
                    color: 'var(--tf-purple)',
                    fontSize: 12,
                    border: '1px solid var(--tf-purple-light)',
                    textDecoration: 'none',
                  }}
                >
                  <ExternalLink size={12} /> Visit
                </a>
              )}
            </div>
          </div>
        ))}
      </div>

      <div
        className="mt-12 p-5 rounded-2xl"
        style={{ background: 'var(--tf-bg-secondary)', border: '1px solid var(--tf-card-border)' }}
      >
        <p
          className="font-body font-bold text-center"
          style={{ fontSize: 12.5, color: 'var(--tf-purple-dark)', lineHeight: 1.55 }}
        >
          If you are in crisis right now or thinking of harming yourself, please call Samaritans on
          <a href="tel:116123" className="tf-link"> 116 123</a> (24/7) or NHS 111 — or 999 in
          immediate danger.
        </p>
      </div>
    </div>
  );
}
